import React, { useState, useEffect } from 'react';
import { useNavigateWithScrollMemory } from '../../hooks/useNavigateWithScrollMemory';
import useTrackPageView from '../../hooks/useTrackPageView';
import { PAGE_TYPE, HOME_SECTION, CONTENT_TYPE } from '../../constants/gaEnums';
import { homeApi } from '../../api/home';
import { useUserStore } from '../../stores/userStore';
import PageLayout from '../../components/layout/PageLayout';
import ProgressBar from '../../components/common/ProgressBar';
import NoData from '../../components/common/NoData';
import AlbumCover from '../../components/common/AlbumCover';
import AlbumTitleLink from '../../components/common/AlbumTitleLink';
import ArtistLink from '../../components/common/ArtistLink';
import ReviewCount from '../../components/common/ReviewCount';
import ReviewerProfile from '../../components/common/ReviewerProfile';
import admarkIcon from '../../assets/admark.png';
import { Stack, Box, Typography, Rating, Divider, useMediaQuery } from '@mui/material';
import theme from '../../theme';
import SEO from '../../components/common/SEO';
import { Helmet } from 'react-helmet-async';
import { SITE_URL } from '../../utils/env';
import { trackHomeContentClick } from '../../lib/gaEvents';
import { REFERRER_TYPE } from '../../constants/gaEnums';
import PlaylistVerticalCard from '../../components/card/PlaylistVerticalCard';
import PostpopRecommendationNote from '../../components/common/PostpopRecommendationNote';

const HomeAdMark = ({ variant = 'compact', sx = {} }) => {
  const size = variant === 'homeFeature' ? { xs: 16, md: 16 } : { xs: 12, md: 12, lg: 12 };

  return (
    <Box
      component="img"
      src={admarkIcon}
      alt="광고"
      sx={{
        width: size,
        height: size,
        display: 'block',
        flexShrink: 0,
        ...sx,
      }}
    />
  );
};

const Home = () => {
  useTrackPageView({ pageType: PAGE_TYPE.HOME });
  const navigate = useNavigateWithScrollMemory();
  const user = useUserStore((state) => state.user);
  const isXs = useMediaQuery(theme.breakpoints.down('md'));
  const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const [homeItems, setHomeItems] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchHomeContent = async () => {
    setLoading(true);
    try {
      const response = await homeApi.getHomeContent();
      setHomeItems(response.data);
    } catch (err) {
      console.error('Error fetching home content:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHomeContent();
  }, []);

  const { koreanMusicReviews, globalMusicReviews, recommendedReviews, latestReviews, latestLists } =
    homeItems;

  const hasContent =
    (koreanMusicReviews && koreanMusicReviews.length > 0) ||
    (globalMusicReviews && globalMusicReviews.length > 0) ||
    (recommendedReviews && recommendedReviews.length > 0) ||
    (latestReviews && latestReviews.length > 0) ||
    (latestLists && latestLists.length > 0);

  const renderReviewGridSection = ({
    title,
    reviews,
    homeSection,
    limit,
    lgColumns,
    xsColumns = 2,
    viewAllPath = '/reviews',
  }) => {
    if (!reviews || reviews.length === 0) return null;
    const isLatestReviewSection = homeSection === HOME_SECTION.LATEST_REVIEWS;
    const visibleLimit = isLatestReviewSection
      ? isXs
        ? Math.min(limit, 6)
        : limit
      : isXs && xsColumns === 3
        ? Math.min(limit, 3)
        : isXs || isMd
          ? Math.min(limit, 4)
          : limit;

    return (
      <Box>
        {renderSectionHeader(title, () => navigate(viewAllPath))}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: `repeat(${xsColumns}, minmax(0, 1fr))`,
              md: isLatestReviewSection ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, 1fr)',
              lg: `repeat(${lgColumns}, 1fr)`,
            },
            columnGap: { xs: xsColumns === 3 ? 1.25 : 2, lg: 2.5 },
            rowGap: {
              xs: isLatestReviewSection ? 3 : xsColumns === 3 ? 2.5 : 3,
              md: isLatestReviewSection ? 3 : 2.5,
              lg: isLatestReviewSection ? 4 : 3.75,
            },
          }}
        >
          {reviews
            .slice(0, visibleLimit)
            .map((review) => renderLatestReviewCard(review, homeSection, reviews))}
        </Box>
      </Box>
    );
  };

  const renderSectionTitle = (title) => {
    const parts = title.split('|');
    if (parts.length !== 2) return title;

    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
        {parts[0].trim()}
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            width: '1px',
            height: '0.62em',
            mx: { xs: 0.8, md: 0.9 },
            backgroundColor: theme.palette.black[3],
            transform: 'translateY(0.02em)',
          }}
        />
        {parts[1].trim()}
      </Box>
    );
  };

  const renderSectionHeader = (title, onViewAll) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: { xs: 2, md: 2.5, lg: 3 },
        gap: 2,
      }}
    >
      <Typography fontWeight={700} sx={{ fontSize: { xs: 20, md: 22, lg: 20 } }}>
        {renderSectionTitle(title)}
      </Typography>
      <Typography
        component="button"
        type="button"
        onClick={onViewAll}
        sx={{
          appearance: 'none',
          border: 0,
          background: 'transparent',
          p: 0,
          color: theme.palette.black[4],
          cursor: 'pointer',
          fontSize: { xs: 13, md: 14 },
          fontWeight: 400,
          lineHeight: 1.5,
          whiteSpace: 'nowrap',
        }}
      >
        더보기
      </Typography>
    </Box>
  );

  const renderCategoryDivider = (sx = {}) => (
    <Box
      sx={{
        height: '1px',
        backgroundColor: theme.palette.grey.line,
        width: '100%',
        ...sx,
      }}
    />
  );

  const getAlbumUrl = (review) =>
    review.albumSlug ? `/reviews/${review.albumSlug}` : `/reviews/local/${review.albumId}`;

  const getUserReviewUrl = (review) => `${getAlbumUrl(review)}/user-reviews/${review.reviewId}`;

  const getAverageReviewLength = (reviews) => {
    if (!reviews?.length) return 140;
    const totalLength = reviews.reduce((sum, review) => sum + (review.content?.length || 0), 0);
    return Math.round(totalLength / reviews.length);
  };

  const getReviewExcerpt = (content, averageLength) => {
    const source = content || '';
    const maxLength = isXs
      ? Math.max(80, Math.min(120, averageLength))
      : Math.max(140, Math.min(220, averageLength));

    if (source.length <= maxLength) {
      return { text: source, truncated: false };
    }

    return {
      text: source.slice(0, maxLength).trimEnd(),
      truncated: true,
    };
  };

  const handleMusicTileClick = (review, homeSection) => {
    trackHomeContentClick(
      {
        section: homeSection,
        contentType: CONTENT_TYPE.ALBUM,
        contentId: review.albumId,
        contentName: review.albumTitle,
      },
      user,
    );
  };

  const handleLatestReviewClick = (review, homeSection) => {
    trackHomeContentClick(
      {
        section: homeSection,
        contentType: CONTENT_TYPE.ALBUM,
        contentId: review.albumId,
        contentName: review.albumTitle,
      },
      user,
    );
  };

  const renderLatestReviewCard = (review, homeSection, reviews) => {
    const albumUrl = getAlbumUrl(review);
    const userReviewUrl = getUserReviewUrl(review);
    const navigateState = { referrerType: REFERRER_TYPE.HOME };
    const excerpt = getReviewExcerpt(review.content, getAverageReviewLength(reviews));

    const navigateToAlbum = () => {
      handleLatestReviewClick(review, homeSection);
      navigate(albumUrl, { state: navigateState });
    };

    const navigateToUserReview = () => {
      handleLatestReviewClick(review, homeSection);
      navigate(userReviewUrl, { state: navigateState });
    };

    const navigateToUser = (nickname) => {
      navigate(`/users/${encodeURIComponent(nickname)}`);
    };

    return (
      <Box
        key={`${homeSection}-${review.reviewId}`}
        sx={{
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 1.5, md: 1.75 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: { xs: 2, md: 1.5, lg: 1.75 },
            minWidth: 0,
            alignItems: 'stretch',
          }}
        >
          <AlbumCover
            imageUrl={review.albumImageUrl}
            altText={review.albumTitle}
            isStarred={review.isStarred}
            onClick={navigateToAlbum}
            sx={{
              width: { xs: '34%', sm: 136, md: 96, lg: 108 },
              minWidth: { xs: 112, md: 0 },
              maxWidth: { xs: 136, md: 'none' },
              aspectRatio: '1 / 1',
              flexShrink: 0,
            }}
          />
          <Box
            sx={{
              minWidth: 0,
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <AlbumTitleLink
              title={review.albumTitle}
              to={albumUrl}
              state={navigateState}
              contentRating={review.albumContentRating}
              onClick={() => handleLatestReviewClick(review, homeSection)}
              lineClamp={1}
              sx={{
                fontSize: { xs: 13, md: 13, lg: 14 },
                lineHeight: 1.22,
                mb: { xs: 0.75, md: 0.75 },
                wordBreak: 'normal',
              }}
              iconSx={{
                width: { xs: 10, lg: 11 },
                height: { xs: 10, lg: 11 },
                ml: 0.5,
                verticalAlign: '-0.05em',
              }}
            />
            <Typography
              component="button"
              type="button"
              onClick={navigateToAlbum}
              sx={{
                appearance: 'none',
                border: 0,
                background: 'transparent',
                p: 0,
                m: 0,
                display: 'block',
                width: '100%',
                textAlign: 'left',
                fontSize: { xs: 12, md: 12, lg: 13 },
                lineHeight: 1.25,
                color: theme.palette.black[4],
                cursor: 'pointer',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {review.artist?.name}
            </Typography>
            <Typography
              component="button"
              type="button"
              onClick={navigateToAlbum}
              sx={{
                appearance: 'none',
                border: 0,
                background: 'transparent',
                p: 0,
                m: 0,
                display: '-webkit-box',
                width: '100%',
                textAlign: 'left',
                fontSize: { xs: 12, md: 12, lg: 13 },
                lineHeight: 1.25,
                color: theme.palette.black[4],
                cursor: 'pointer',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 1,
                mt: 0.75,
              }}
            >
              {review.albumGenres?.slice(0, 3).join(', ')}
            </Typography>
            <Stack spacing={{ xs: 0.5, md: 0.75 }} sx={{ minWidth: 0, width: '100%', mt: 1.25 }}>
              <Box
                component="button"
                type="button"
                onClick={() => navigateToUser(review.userNickname)}
                sx={{
                  appearance: 'none',
                  border: 0,
                  background: 'transparent',
                  p: 0,
                  m: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0,
                  minWidth: 0,
                  maxWidth: '100%',
                  textAlign: 'left',
                  cursor: 'pointer',
                }}
              >
                <ReviewerProfile
                  imageUrl={review.userProfileImageUrl}
                  name={review.userNickname}
                  rating={review.rating}
                  grade={review.userGrade}
                />
              </Box>
            </Stack>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.25, md: 0.5 } }}>
          <Typography
            component="button"
            type="button"
            onClick={navigateToUserReview}
            sx={{
              appearance: 'none',
              border: 0,
              background: 'transparent',
              p: 0,
              m: 0,
              width: '100%',
              textAlign: 'left',
              color: theme.palette.black[7],
              fontSize: { xs: 13, md: 13, lg: 13.5 },
              lineHeight: 1.5,
              cursor: 'pointer',
              wordBreak: 'break-word',
              display: '-webkit-box',
              WebkitLineClamp: { xs: 2, md: 3 },
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {excerpt.text}
            {excerpt.truncated && (
              <Box component="span" sx={{ color: theme.palette.black[4], whiteSpace: 'nowrap' }}>
                ...더보기
              </Box>
            )}
          </Typography>
          {review.createdAt && (
            <Typography
              color={theme.palette.black[1]}
              sx={{
                fontSize: { xs: 12, md: 12 },
                lineHeight: 1.25,
              }}
            >
              {review.createdAt}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  const renderMusicTileCard = (review, homeSection, options = {}) => {
    const albumUrl = getAlbumUrl(review);
    const navigateState = { referrerType: 'home' };
    const albumAvgRating = Number(review.albumAvgRating || 0);
    const albumReviewCount = Number(review.albumReviewCount || 0);
    const { hideReviewCountOnMobile = false, hideRatingRow = false, cardSx = {} } = options;

    return (
      <Box
        key={`${homeSection}-${review.reviewId}`}
        sx={{
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          ...cardSx,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: '1 / 1',
            mb: { xs: 1, md: 1.25, lg: 1.5 },
          }}
        >
          <AlbumCover
            imageUrl={review.albumImageUrl}
            altText={review.albumTitle}
            isStarred={review.isStarred}
            onClick={() => {
              handleMusicTileClick(review, homeSection);
              navigate(albumUrl, { state: navigateState });
            }}
            sx={{
              width: '100%',
              aspectRatio: '1 / 1',
            }}
          />
        </Box>
        <AlbumTitleLink
          title={review.albumTitle}
          to={albumUrl}
          state={navigateState}
          contentRating={review.albumContentRating}
          onClick={() => handleMusicTileClick(review, homeSection)}
          lineClamp={1}
          sx={{
            fontSize: { xs: 13, md: 13, lg: 14 },
            lineHeight: 1.25,
            minHeight: { xs: 16.25, md: 16.25, lg: 17.5 },
            mb: 0.75,
            wordBreak: 'normal',
          }}
          iconSx={{
            width: { xs: 10, lg: 11 },
            height: { xs: 10, lg: 11 },
            ml: 0.5,
            verticalAlign: '-0.05em',
          }}
        />
        <ArtistLink
          artistName={review.artist?.name}
          artistSource={review.artist?.artistSource}
          artistSourceId={review.artist?.sourceId}
          artistSlug={review.artist?.slug}
          artistDbId={review.artist?.id}
          sx={{
            fontSize: { xs: 12, md: 12, lg: 13 },
            color: theme.palette.black[4],
            lineHeight: 1.25,
            minHeight: { xs: 15, md: 15, lg: 16.25 },
            mb: 0.75,
            WebkitLineClamp: 1,
          }}
        />
        <Typography
          sx={{
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: { xs: 12, md: 12, lg: 13 },
            lineHeight: 1.25,
            minHeight: { xs: 15, md: 15, lg: 16.25 },
            color: theme.palette.black[4],
            mb: 0.75,
          }}
        >
          {review.albumGenres?.slice(0, 3).join(', ')}
        </Typography>
        <Stack spacing={0.75}>
          <Typography
            fontSize={{ xs: 12, lg: 13 }}
            lineHeight={1.25}
            color={theme.palette.black[4]}
            sx={{ minHeight: { xs: 15, lg: 16.25 } }}
          >
            {review.albumReleaseDate}
          </Typography>
          {!hideRatingRow && (
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minHeight: 18 }}>
              <Rating
                value={albumAvgRating}
                precision={0.5}
                readOnly
                size="small"
                sx={{
                  color: theme.palette.secondary.main,
                  fontSize: { xs: 13, lg: 14 },
                  '& .MuiRating-iconEmpty': {
                    color: theme.palette.grey[4],
                  },
                }}
              />
              <Typography
                fontSize={{ xs: 11, lg: 12 }}
                lineHeight={1}
                color={theme.palette.black[3]}
                sx={{ whiteSpace: 'nowrap' }}
              >
                ({albumAvgRating.toFixed(1)})
              </Typography>
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  display: hideReviewCountOnMobile ? { xs: 'none', sm: 'block' } : 'block',
                  height: 8,
                  alignSelf: 'center',
                  backgroundColor: theme.palette.grey.line,
                }}
              />
              <Box
                sx={{ display: hideReviewCountOnMobile ? { xs: 'none', sm: 'block' } : 'block' }}
              >
                {review.isAdMarked ? (
                  <HomeAdMark variant="compact" />
                ) : (
                  <ReviewCount reviewCount={albumReviewCount} variant="compact" />
                )}
              </Box>
            </Stack>
          )}
        </Stack>
      </Box>
    );
  };

  const renderMusicPanel = ({ title, reviews, homeSection, viewAllPath }) => {
    if (!reviews || reviews.length === 0) return null;

    return (
      <Box
        sx={{
          minWidth: 0,
        }}
      >
        {renderSectionHeader(title, () => navigate(viewAllPath))}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            },
            columnGap: { xs: 2, md: 2, lg: 2.25 },
            rowGap: { xs: 3, md: 3, lg: 4 },
            alignItems: 'start',
          }}
        >
          {reviews.slice(0, 6).map((review) => renderMusicTileCard(review, homeSection))}
        </Box>
      </Box>
    );
  };

  const getRecommendationComment = (review) =>
    review.recommendationComment ||
    review.postpopComment ||
    review.editorComment ||
    review.content ||
    '포스트팝이 고른 추천 앨범입니다.';

  const renderRecommendedMemberReview = (review) => {
    if (!review?.content) return null;

    return (
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          mt: { md: 1.5, lg: 1.75 },
          pt: { md: 1.25, lg: 1.5 },
          borderTop: `1px solid ${theme.palette.grey.line}`,
        }}
      >
        <ReviewerProfile
          imageUrl={review.userProfileImageUrl}
          name={review.userNickname}
          rating={review.rating}
          grade={review.userGrade}
        />
        <Typography
          sx={{
            mt: { md: 1, lg: 1.125 },
            color: theme.palette.black[6],
            fontSize: { md: 13, lg: 13.5 },
            lineHeight: 1.5,
            wordBreak: 'break-word',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {review.content}
        </Typography>
      </Box>
    );
  };

  const renderRecommendedFeatureCard = (review) => {
    const albumUrl = getAlbumUrl(review);
    const navigateState = { referrerType: REFERRER_TYPE.HOME };
    const albumAvgRating = Number(review.albumAvgRating || 0);
    const albumReviewCount = Number(review.albumReviewCount || 0);

    const navigateToAlbum = () => {
      handleMusicTileClick(review, HOME_SECTION.RECOMMENDED);
      navigate(albumUrl, { state: navigateState });
    };

    return (
      <Box
        component="div"
        role="button"
        tabIndex={0}
        onClick={navigateToAlbum}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            navigateToAlbum();
          }
        }}
        sx={{
          appearance: 'none',
          border: 0,
          p: 0,
          width: '100%',
          minWidth: 0,
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'minmax(0, 1fr) minmax(0, 2fr)',
            lg: '250px minmax(0, 1fr)',
          },
          gap: { xs: 1.5, md: 2.5, lg: 2.5 },
          alignItems: { xs: 'start', md: 'center' },
          backgroundColor: 'transparent',
          textAlign: 'left',
          cursor: 'pointer',
          color: theme.palette.black[9],
          mb: { xs: 1, md: 0 },
        }}
      >
        <Box sx={{ minWidth: 0, width: '100%' }}>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: '1 / 1',
            }}
          >
            <AlbumCover
              imageUrl={review.albumImageUrl}
              altText={review.albumTitle}
              isStarred={review.isStarred}
              sx={{
                width: '100%',
                aspectRatio: '1 / 1',
              }}
            />
          </Box>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <AlbumTitleLink
            title={review.albumTitle}
            contentRating={review.albumContentRating}
            lineClamp={1}
            sx={{
              fontSize: { xs: 16, md: 22, lg: 24 },
              lineHeight: 1.2,
              mb: { xs: 1.125, md: 1.25 },
              fontWeight: 700,
            }}
            iconSx={{
              width: { xs: 12, md: 14 },
              height: { xs: 12, md: 14 },
              ml: 0.5,
              verticalAlign: '-0.05em',
            }}
          />
          <ArtistLink
            artistName={review.artist?.name}
            artistSource={review.artist?.artistSource}
            artistSourceId={review.artist?.sourceId}
            artistSlug={review.artist?.slug}
            artistDbId={review.artist?.id}
            sx={{
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: { xs: 14, md: 14, lg: 15 },
              color: theme.palette.black[4],
              lineHeight: 1.25,
              mb: { xs: 1.125, md: 1.5 },
            }}
          />
          <Typography
            sx={{
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: { xs: 14, md: 14, lg: 15 },
              color: theme.palette.black[4],
              lineHeight: 1.25,
              mb: { xs: 1.125, md: 1.5 },
            }}
          >
            {review.albumGenres?.slice(0, 3).join(', ')}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 14, md: 14, lg: 15 },
              color: theme.palette.black[4],
              lineHeight: 1.25,
              mb: { xs: 1.125, md: 1.5 },
              whiteSpace: 'nowrap',
            }}
          >
            {review.albumReleaseDate}
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{ minHeight: 18, mb: { xs: 1.5, md: 2 } }}
          >
            <Rating
              value={albumAvgRating}
              precision={0.5}
              readOnly
              size="small"
              sx={{
                color: theme.palette.secondary.main,
                fontSize: { xs: 14, md: 14 },
                '& .MuiRating-iconEmpty': {
                  color: theme.palette.grey[4],
                },
              }}
            />
            <Typography
              fontSize={{ xs: 14, md: 14 }}
              lineHeight={1}
              color={theme.palette.black[3]}
              sx={{ whiteSpace: 'nowrap' }}
            >
              ({albumAvgRating.toFixed(1)})
            </Typography>
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                height: 8,
                alignSelf: 'center',
                backgroundColor: theme.palette.grey.line,
              }}
            />
            {review.isAdMarked ? (
              <HomeAdMark variant="homeFeature" />
            ) : (
              <ReviewCount reviewCount={albumReviewCount} variant="homeFeature" />
            )}
          </Stack>
          <PostpopRecommendationNote
            comment={getRecommendationComment(review)}
            sx={{
              display: { xs: 'none', md: 'flex' },
              mt: { md: 1.25, lg: 1.5 },
            }}
          />
          {renderRecommendedMemberReview(review)}
          <PostpopRecommendationNote
            comment={getRecommendationComment(review)}
            sx={{
              display: { xs: 'flex', md: 'none' },
              mt: 0.5,
              mb: 1.25,
            }}
          />
        </Box>
      </Box>
    );
  };

  const renderRecommendedSection = ({ title, reviews, viewAllPath }) => {
    if (!reviews || reviews.length === 0) return null;
    const [feature, ...rest] = reviews;
    const miniReviews = rest.slice(0, isXs ? 4 : 6);

    return (
      <Box>
        {renderSectionHeader(title, () => navigate(viewAllPath))}
        <Stack spacing={{ xs: 2.25, md: 2.75, lg: 3 }}>
          {renderRecommendedFeatureCard(feature)}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(3, minmax(0, 1fr))',
                lg: 'repeat(6, minmax(0, 1fr))',
              },
              columnGap: { xs: 2, md: 2, lg: 2.25 },
              rowGap: { xs: 2.5, md: 3, lg: 4 },
              alignItems: 'start',
            }}
          >
            {miniReviews.map((review) => renderMusicTileCard(review, HOME_SECTION.RECOMMENDED))}
          </Box>
        </Stack>
      </Box>
    );
  };

  const renderFeaturedListCard = (playlist) => (
    <PlaylistVerticalCard
      key={`featured-list-${playlist.id}`}
      playlist={playlist}
      thumbnailVariant="square"
      homeSection={HOME_SECTION.LISTS}
      titleLineClamp={1}
    />
  );

  return (
    <PageLayout>
      <SEO title="Postpop. Your Own Archive" rawTitle />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'Postpop',
            alternateName: '포스트팝',
            url: SITE_URL,
            logo: `${SITE_URL}/favicon.png`,
            description:
              '포스트팝은 음악 리뷰 커뮤니티입니다. 앨범 리뷰와 함께 최신 음악 뉴스, 플레이리스트 만들기까지. 나만의 음악 취향을 아카이빙 해보세요.',
            email: 'contact@postpop.kr',
            address: {
              '@type': 'PostalAddress',
              streetAddress: '성지길 36-5 1층 104호',
              addressLocality: '마포구',
              addressRegion: '서울',
              addressCountry: 'KR',
            },
            sameAs: ['https://www.instagram.com/postpop.kr/'],
          })}
        </script>
      </Helmet>
      <ProgressBar isLoading={loading}>
        {!loading && !hasContent ? (
          <NoData />
        ) : (
          <Stack spacing={{ xs: 3.75, lg: 6.25 }}>
            {/* 앨범/리뷰 섹션 */}
            {((koreanMusicReviews && koreanMusicReviews.length > 0) ||
              (globalMusicReviews && globalMusicReviews.length > 0) ||
              (recommendedReviews && recommendedReviews.length > 0) ||
              (latestReviews && latestReviews.length > 0)) && (
              <Stack spacing={{ xs: 3.75, lg: 6.25 }}>
                {(koreanMusicReviews?.length > 0 || globalMusicReviews?.length > 0) && (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        lg:
                          koreanMusicReviews?.length > 0 && globalMusicReviews?.length > 0
                            ? 'minmax(0, 1fr) 1px minmax(0, 1fr)'
                            : '1fr',
                      },
                      columnGap: { lg: 3 },
                      rowGap: { xs: 4.5, md: 5 },
                      alignItems: 'start',
                    }}
                  >
                    {renderMusicPanel({
                      title: '최신 앨범 | 국내',
                      reviews: koreanMusicReviews,
                      homeSection: HOME_SECTION.KOREAN_MUSIC,
                      viewAllPath:
                        '/albums?region=korean&category=releases&type=albums&sort=popular&period=week&page=1',
                    })}

                    {koreanMusicReviews?.length > 0 && globalMusicReviews?.length > 0 && (
                      <Box
                        sx={{
                          display: 'block',
                          width: '100%',
                          height: { xs: '1px', lg: 'auto' },
                          alignSelf: 'stretch',
                          backgroundColor: theme.palette.grey.line,
                        }}
                      />
                    )}

                    {renderMusicPanel({
                      title: '최신 앨범 | 해외',
                      reviews: globalMusicReviews,
                      homeSection: HOME_SECTION.GLOBAL_MUSIC,
                      viewAllPath:
                        '/albums?region=global&category=releases&type=albums&sort=popular&period=week&page=1',
                    })}
                  </Box>
                )}

                {(koreanMusicReviews?.length > 0 || globalMusicReviews?.length > 0) &&
                  recommendedReviews?.length > 0 &&
                  renderCategoryDivider({ my: { xs: 0.75, lg: 0 } })}

                {renderRecommendedSection({
                  title: '포스트팝 추천',
                  reviews: recommendedReviews,
                  viewAllPath:
                    '/albums?region=all&category=recommended&type=albums&sort=latest&page=1',
                })}

                {(koreanMusicReviews?.length > 0 ||
                  globalMusicReviews?.length > 0 ||
                  recommendedReviews?.length > 0) &&
                  latestReviews?.length > 0 &&
                  renderCategoryDivider({ my: { xs: 0.75, lg: 0 } })}

                {renderReviewGridSection({
                  title: '최근 리뷰',
                  reviews: latestReviews,
                  homeSection: HOME_SECTION.LATEST_REVIEWS,
                  limit: 8,
                  lgColumns: 4,
                  xsColumns: 1,
                  viewAllPath:
                    '/reviews?region=all&category=recent&type=all&sort=latest&period=month&page=1',
                })}
              </Stack>
            )}

            {/* 믹스 섹션 */}
            {latestLists?.length > 0 && (
              <Stack spacing={{ xs: 3.75, lg: 6.25 }}>
                {latestReviews?.length > 0 && renderCategoryDivider({ my: { xs: 0.75, lg: 0 } })}
                <Box>
                  {renderSectionHeader('플리', () => navigate('/lists'))}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'repeat(2, minmax(0, 1fr))',
                        md: 'repeat(3, minmax(0, 1fr))',
                        lg: 'repeat(6, minmax(0, 1fr))',
                      },
                      columnGap: { xs: 1.5, md: 2, lg: 2.25 },
                      rowGap: { xs: 3, md: 2.5, lg: 3 },
                    }}
                  >
                    {latestLists.slice(0, 6).map(renderFeaturedListCard)}
                  </Box>
                </Box>
              </Stack>
            )}
          </Stack>
        )}
      </ProgressBar>
    </PageLayout>
  );
};

export default Home;
